/**
 * TransmissionSection — Displays linked executions for a payment.
 *
 * The new payment response has executionIds: { PAIN_001: [...], PAIN002: [...] }.
 * This section shows them grouped into Outbound (PAIN_001) and Bank Responses (others).
 */
import { useMemo, useState } from 'react';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import Tabs from '@cloudscape-design/components/tabs';
import { useAppSelector } from 'src/store/hooks';
import { ExecutionIdsMap } from 'src/store/slices/payment/paymentState';
import { CopyableText } from 'src/components/common/CopyableText';
import { TransmissionDetailPanel } from './TransmissionDetailPanel';

/** Row for the execution tables. */
interface ExecutionRow {
  executionArn: string;
  executionType: string;
}

/** Builds rows from executionIds map for a given set of types. */
const buildExecutionRows = (executionIds: ExecutionIdsMap, types: string[]): ExecutionRow[] => {
  const rows: ExecutionRow[] = [];
  types.forEach(type => {
    const ids = executionIds[type];
    if (ids) {
      ids.forEach(id => rows.push({ executionArn: id, executionType: type }));
    }
  });
  return rows;
};

/** Column definitions for execution tables. */
const getColumns = (selectedArn: string | null, onToggleDetails: (item: ExecutionRow) => void) => [
  { id: 'executionType', header: 'Type', cell: (item: ExecutionRow) => <Badge>{item.executionType}</Badge> },
  { id: 'executionArn', header: 'Execution ID', cell: (item: ExecutionRow) => <CopyableText value={item.executionArn} /> },
  { id: 'actions', header: '', cell: (item: ExecutionRow) => (
    <Button
      variant="inline-link"
      disabled={item.executionArn == null}
      onClick={() => onToggleDetails(item)}
    >
      {selectedArn != null && selectedArn === item.executionArn ? 'Hide' : 'Details'}
    </Button>
  )},
];

export const TransmissionSection = () => {
  const payment = useAppSelector(state => state.payment.data);
  const [selectedItem, setSelectedItem] = useState<ExecutionRow | null>(null);
  const [activeTabId, setActiveTabId] = useState('outbound');

  const executionIds = useMemo(() => payment?.executionIds ?? {}, [payment?.executionIds]);

  // Outbound = PAIN_001 type
  const outboundRows = useMemo(
    () => buildExecutionRows(executionIds, ['PAIN_001']),
    [executionIds],
  );

  // Bank responses = everything except PAIN_001
  const bankResponseRows = useMemo(() => {
    const otherTypes = Object.keys(executionIds).filter(t => t !== 'PAIN_001');
    return buildExecutionRows(executionIds, otherTypes);
  }, [executionIds]);

  if (outboundRows.length === 0 && bankResponseRows.length === 0) return null;

  const handleToggleDetails = (item: ExecutionRow) => {
    const isSameItem = item.executionArn != null
      && selectedItem?.executionArn === item.executionArn;
    setSelectedItem(isSameItem ? null : item);
  };

  const handleTabChange = ({ detail }: { detail: { activeTabId: string } }) => {
    setActiveTabId(detail.activeTabId);
    setSelectedItem(null);
  };

  const selectedArn = selectedItem?.executionArn ?? null;

  const renderWithPanel = (table: React.ReactNode) => {
    if (!selectedItem) return table;
    return (
      <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
        {table}
        <TransmissionDetailPanel item={selectedItem} onClose={() => setSelectedItem(null)} />
      </Grid>
    );
  };

  return (
    <Container header={<Header variant="h2">Executions</Header>}>
      <Tabs
        activeTabId={activeTabId}
        onChange={handleTabChange}
        tabs={[
          {
            id: 'outbound',
            label: `Outbound (${outboundRows.length})`,
            content: outboundRows.length > 0 ? renderWithPanel(
              <Table
                variant="embedded"
                sortingDisabled
                resizableColumns
                columnDefinitions={getColumns(selectedArn, handleToggleDetails)}
                items={outboundRows}
                empty={<Box textAlign="center" color="text-body-secondary">No outbound executions</Box>}
              />
            ) : <Box color="text-body-secondary">No outbound executions</Box>,
          },
          {
            id: 'bank-responses',
            label: `Bank Responses (${bankResponseRows.length})`,
            content: bankResponseRows.length > 0 ? renderWithPanel(
              <Table
                variant="embedded"
                sortingDisabled
                resizableColumns
                columnDefinitions={getColumns(selectedArn, handleToggleDetails)}
                items={bankResponseRows}
                empty={<Box textAlign="center" color="text-body-secondary">No bank response executions</Box>}
              />
            ) : <Box color="text-body-secondary">No bank response executions yet</Box>,
          },
        ]}
      />
    </Container>
  );
};
